<template>
    <b-modal :visible="value" @change="onVisibleChange" title="Statement Delivery" hide-footer>
        <template v-if="statement">
            <b-table-simple small borderless class="mb-2">
                <tbody>
                    <tr>
                        <th class="pl-0" style="width: 40%;">Statement Date</th>
                        <td>{{ formatDate(statement.statementDate) }}</td>
                    </tr>
                    <tr>
                        <th class="pl-0">Period</th>
                        <td>{{ formatDate(statement.periodStart) }} &ndash; {{ formatDate(statement.periodEnd) }}</td>
                    </tr>
                    <tr>
                        <th class="pl-0">Status</th>
                        <td>
                            <b-badge :variant="statusVariant">{{ statusLabel }}</b-badge>
                        </td>
                    </tr>
                    <tr>
                        <th class="pl-0">Delivery Channels</th>
                        <td>{{ deliveryMethodsLabel }}</td>
                    </tr>
                    <tr>
                        <th class="pl-0">Delivered</th>
                        <td>{{ formatDateTime(statement.deliveredDate) || "Not delivered" }}</td>
                    </tr>
                    <tr>
                        <th class="pl-0">Archived PDF</th>
                        <td>{{ hasPdf ? "Available" : "Not rendered yet" }}</td>
                    </tr>
                </tbody>
            </b-table-simple>

            <b-alert :show="!hasPdf" variant="warning" class="py-2 mb-2">
                This statement has no archived PDF. Re-sending renders and archives it, then delivers
                it through the account's configured channels.
            </b-alert>

            <b-alert :show="hasPdf && !statement.deliveredDate" variant="warning" class="py-2 mb-2">
                The PDF was archived but no delivery channel succeeded. {{ noDeliveryChannelReason }}
            </b-alert>
        </template>

        <div class="text-right mt-2">
            <b-button variant="secondary" @click="close">Close</b-button>
        </div>
    </b-modal>
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";
import moment from "moment";
import {
    ARStatementSummary,
    arNoDeliveryChannelReason,
    arStatementDeliveryMethodsLabel,
    arStatementStatusLabel,
    arStatementStatusVariant,
} from "@/models/AR";

@Component({
    name: "ARStatementDeliveryModal",
})
export default class ARStatementDeliveryModal extends Vue {
    @Prop({ type: Boolean, default: false }) value!: boolean;
    @Prop({ default: null }) statement!: ARStatementSummary | null;

    get noDeliveryChannelReason(): string {
        return arNoDeliveryChannelReason;
    }

    get statusLabel(): string {
        return arStatementStatusLabel(this.statement?.status);
    }

    get statusVariant(): string {
        return arStatementStatusVariant(this.statement?.status);
    }

    get deliveryMethodsLabel(): string {
        return arStatementDeliveryMethodsLabel(this.statement?.deliveryMethods);
    }

    // The archive path is written before delivery is attempted, so its presence is what tells the
    // user whether there is a document to open — not the Delivered status.
    get hasPdf(): boolean {
        return !!this.statement?.pdfPath;
    }

    /** The statement date and period bounds are date-only, so they are formatted without conversion. */
    formatDate(date?: string | null): string {
        return date ? moment(date).format("L") : "";
    }

    /** The delivery timestamp is a real instant recorded in UTC, so it is shown in the viewer's zone. */
    formatDateTime(date?: string | null): string {
        return date ? moment.utc(date).local().format("L LT") : "";
    }

    onVisibleChange(visible: boolean): void {
        this.$emit("input", visible);
    }

    close(): void {
        this.$emit("input", false);
    }
}
</script>

<style scoped></style>
